import React, { useEffect, useState } from "react";
import { Ellipsis, Search, X } from "lucide-react";
import { Badge, Button, FormCheck, Form, Spinner } from "react-bootstrap";
import { ColumnDef, OnChangeFn, SortingState } from "@tanstack/react-table";

import DebouncedInput from "Components/Common/DebouncedInput";
import { StyledTable } from "Components/Common/StyledTable";
import { AngleLeftIcon } from "Components/Icons/AngleLeftIcon";
import DotIcon from "Components/Icons/Dot";
import { EllipsisVerticalIcon } from "Components/Icons/EllipsisVerticalIcon";

import { openDropdownStatus } from "./CourseCatalog";
import CoursesModal from "./CoursesModal";

import { useGetCourseCatalog, useGetUnmappedSections } from "@/hooks/course-catalog";

import { EAIClient } from "Services/EAIClient";
import type { AxiosResponse } from "axios";

import type { CoursesCatalogInterface, SectionsCatalogInterface, SuggestedCourseInterface } from "Stores/courseStore";
import { useAppContext } from "@/contexts/app";
import { useToast } from "@/hooks/useToast";
import { StyledModal } from "Components/Common/StyledModal";

interface IMapSectionResult {
    result: {
        code: number;
        message: string;
    };
}

export interface UnmappedSectionsProps {
    setShowCourseCatalog: () => void;
    handleCreateCourse: () => void;
    refreshCourseCatalog: () => Promise<void>;
    isLoading?: boolean;
}
export interface unmappedTableState {
    sections: SectionsCatalogInterface[] | undefined;
    courses: CoursesCatalogInterface[] | undefined;
    suggestedCourses: SuggestedCourseInterface[] | undefined;
    isOverrideOpen: openDropdownStatus;
    dropdownOpen: openDropdownStatus;
    showAll: boolean;
    openMap: boolean;
    isMapping: boolean;
    confirmMapping: boolean;
    sectionsSelection: number[];
    courseSelection: string;
    selectedCourse: CoursesCatalogInterface | null;
    sectionsPage: number;
    pageSize: number;
    sorting: SortingState;
    sortBy: string;
    sortOrder: "asc" | "desc" | "";
    sectionsTitle: string;
    refreshCourses: boolean;
}

interface unmappedTableProps {
    columns: ColumnDef<SuggestedCourseInterface>[] | ColumnDef<CoursesCatalogInterface>[] | ColumnDef<SectionsCatalogInterface>[];
    data: SuggestedCourseInterface[] | CoursesCatalogInterface[] | SectionsCatalogInterface[];
    noDataMessage: string;
    className: string;
    totalItems?: number;
    currentPage?: number;
    setCurrentPage?: (page: number) => void;
    pageSize?: number;
    setPageSize?: (pageSize: number) => void;
    sorting?: SortingState;
    setSorting?: OnChangeFn<SortingState>
    isLoading?: boolean;
    refresh?: boolean;
}

const StatusBadge = ({ isDisabled }: { isDisabled: boolean }) => {
    return (
        <div className="badge-container">
            {isDisabled ? (
                <Badge bg="danger">
                    <DotIcon className="dot" /> Disabled
                </Badge>
            ) : (
                <Badge bg="success">
                    <DotIcon className="dot" /> Enabled
                </Badge>
            )}
        </div>
    );
};

export const UnmappedSections = (props: UnmappedSectionsProps) => {
    const eaiClient = new EAIClient();
    const appContext = useAppContext();
    const [state, setState] = useState<unmappedTableState>({
        sections: [],
        courses: [],
        suggestedCourses: [],
        isOverrideOpen: { id: "", open: false },
        dropdownOpen: { open: false, id: "" },
        showAll: true,
        openMap: false,
        isMapping: false,
        confirmMapping: false,
        sectionsSelection: [],
        courseSelection: "",
        selectedCourse: null,
        sectionsPage: 1,
        pageSize: 10,
        sorting: [],
        sortBy: "",
        sortOrder: "",
        sectionsTitle: "",
        refreshCourses: false,
    });

    const { data: unmappedSections, mutate: mutateUnmappedSections, isLoading: isLoadingUnmappedSections } = useGetUnmappedSections(state.sectionsPage, state.sectionsTitle, state.pageSize, state.sortBy, state.sortOrder);
    const { data: courseCatalog } = useGetCourseCatalog();
    const { toast } = useToast();

    const [disableModalOpen, setDisableModalOpen] = useState(false);
    const [selectedSection, setSelectedSection] = useState<SectionsCatalogInterface | null>(null);
    const [disableReason, setDisableReason] = useState("");
    const [menuOpen, setMenuOpen] = useState<{ id: string, open: boolean }>({ id: "", open: false });
    const [localOverrides, setLocalOverrides] = useState<Set<number>>(new Set());
    const [disableLoading, setDisableLoading] = useState(false);

    const getSelectedLength = () => state.sectionsSelection.length;

    const getSelectedCourseName = () => `${state.selectedCourse?.subject_number + " - " + state.selectedCourse?.course_title}`;

    const getSuccessMessage = () => {
        return `${getSelectedLength() ?? ""} section${getSelectedLength() > 1 ? "s" : ""} have been mapped to ${getSelectedCourseName() ?? ""}`;
    };

    useEffect(() => {
        const storedOverrides = sessionStorage.getItem('unmappedSectionLocalOverrides');
        if (storedOverrides) {
            setLocalOverrides(new Set(JSON.parse(storedOverrides)));
        }
    }, []);

    const handleMenuClick = (rowId: number) => {
        setMenuOpen(prev => ({ id: prev.id === String(rowId) ? "" : String(rowId), open: prev.id !== String(rowId) ? true : false }));
    };
    const handleDisable = (section: SectionsCatalogInterface) => {
        setSelectedSection(section);
        setDisableReason("");
        setDisableModalOpen(true);
        setMenuOpen({ id: "", open: false });
    };
    const handleDisableSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!selectedSection || !disableReason || !unmappedSections) return;
        setDisableLoading(true);

        const optimisticData = {
            ...(unmappedSections ?? { data: { data: [] } }),
            data: {
                ...(unmappedSections?.data ?? { data: [] }),
                data: (unmappedSections?.data.data ?? []).map(section =>
                    section.id === selectedSection.id ? { ...section, disabled: !section.disabled } : section
                ),
            },
        };

        try {
            // This is the definitive payload for an unmapped section. It correctly uses `null` for
            // the missing course_id and conditionally includes term_id only if it exists. This
            // structure is logically sound and robust from the frontend's perspective.
            const payload: Record<string, any> = {
                name: selectedSection.name ?? "",
                course_id: selectedSection.course_id ?? null,
                disabled: !selectedSection.disabled,
                course: selectedSection.course ?? "",
                course_short_name: selectedSection.course_short_name ?? "",
                curriculum_year: selectedSection.curriculum_year ?? "",
                course_title: selectedSection.course_title ?? "",
                subject_number: selectedSection.subject_number ?? "",
                credit_hours: selectedSection.credit_hours ?? 0,
                needs_review: false,
                override_comment: disableReason
            };

            // From our debugging history, we know the backend has issues with term_id on unmapped
            // sections. Conditionally including it is the safest approach.
            if (selectedSection.term_id) {
                payload.term_id = selectedSection.term_id;
            }

            // Perform an optimistic update. We trust the UI change and let SWR handle background
            // revalidation later. This prevents the UI flicker from a premature re-fetch.
            await mutateUnmappedSections(
                eaiClient.ax.put(
                    `${eaiClient.endpoint}/schools/${eaiClient.school}/course_catalog/section/${selectedSection.id}/`,
                    payload
                ),
                {
                    optimisticData: optimisticData,
                    rollbackOnError: true,
                    populateCache: false,
                    revalidate: false, // This is key to preventing the flicker.
                }
            );

            toast({
                type: "success",
                title: "Section Updated Successfully",
                message: `Section ${selectedSection.name} has been ${selectedSection.disabled ? "activated" : "deactivated"}`
            });

            // Refresh the main course catalog to ensure other parts of the UI are updated. This
            // does not re-fetch the unmapped sections, so it won't cause a flicker.
            await props.refreshCourseCatalog();

            // Force the UI state to be kept locally by saving the override to sessionStorage.
            const newOverrides = new Set(localOverrides);
            if (!selectedSection.disabled) { // If we just disabled it
                newOverrides.add(selectedSection.id);
            } else { // If we just enabled it
                newOverrides.delete(selectedSection.id);
            }
            setLocalOverrides(newOverrides);
            sessionStorage.setItem('unmappedSectionLocalOverrides', JSON.stringify(Array.from(newOverrides)));
        } catch (error) {
            appContext.dispatchError(error);
        } finally {
            setDisableModalOpen(false);
            setSelectedSection(null);
            setDisableReason("");
            setDisableLoading(false);
        }
    };

    const columnsSections: ColumnDef<SectionsCatalogInterface>[] = [
        {
            header: "",
            accessorKey: "id",
            cell: (cellProps) => {
                const isEffectivelyDisabled = cellProps.row.original.disabled || localOverrides.has(cellProps.row.original.id);
                return (
                    <div className="checkbox-col">
                        <FormCheck name="sectionCheck" className="radio-check" checked={state.sectionsSelection.includes(cellProps.row.original.id)} onChange={(e) => setSectionSelection(cellProps.row.original.id, e.target.checked)} disabled={isEffectivelyDisabled} />
                    </div>
                );
            },
        },
        {
            header: "Subject Number",
            accessorKey: "subject_number",
            cell: (cellProps) => {
                const isEffectivelyDisabled = cellProps.row.original.disabled || localOverrides.has(cellProps.row.original.id);
                return <div className={isEffectivelyDisabled ? "labeldisabled" : ""}>{cellProps.row.original.subject_number ?? "-"}</div>;
            },
        },
        {
            header: "Section Title",
            accessorKey: "name",
            cell: (cellProps) => {
                const isEffectivelyDisabled = cellProps.row.original.disabled || localOverrides.has(cellProps.row.original.id);
                return (
                    <div className={state.sectionsSelection.includes(cellProps.row.original.id) ? "selected-row" : ""}>
                        <span className={isEffectivelyDisabled ? "labeldisabled" : ""}>{cellProps.row.original.name}</span>
                    </div>
                );
            },
        },
        {
            header: "Term",
            accessorKey: "term_name",
            cell: (cellProps) => {
                const isEffectivelyDisabled = cellProps.row.original.disabled || localOverrides.has(cellProps.row.original.id);
                return <div className={isEffectivelyDisabled ? "labeldisabled" : ""}>{cellProps.row.original.term_name ?? "-"}</div>;
            },
        },
        {
            header: "Status",
            accessorKey: "disabled",
            cell: (cellProps) => {
                const isEffectivelyDisabled = cellProps.row.original.disabled || localOverrides.has(cellProps.row.original.id);
                return <StatusBadge isDisabled={isEffectivelyDisabled} />;
            },
        },
        {
            id: "actions",
            header: "",
            cell: ({ row }) => {
                const section = row.original;
                const isEffectivelyDisabled = section.disabled || localOverrides.has(section.id);
                return (
                    <div className="d-flex justify-content-end">
                        <div className="position-relative">
                            <Button
                                variant="ghost"
                                className="p-1"
                                onClick={() => handleMenuClick(section.id)}
                            >
                                <EllipsisVerticalIcon className="icon" />
                            </Button>
                            {menuOpen.id === String(section.id) && menuOpen.open && (
                                <div className="dropdown-menu show position-absolute end-0">
                                    <Button
                                        variant="ghost"
                                        className="dropdown-item"
                                        onClick={() => handleDisable(section)}
                                    >
                                        {isEffectivelyDisabled ? "Activate" : "Deactivate"}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
        }
    ];

    useEffect(() => {
        if (state.sorting.length > 0) {
            setState((prev) => ({ ...prev, sortBy: state.sorting[0].id, sortOrder: state.sorting[0].desc ? "desc" : "asc" }));
        } else {
            setState(prevState => ({
                ...prevState,
                sortBy: "",
                sortOrder: "",
            }));
        }
    }, [state.sorting]);

    const getUnmappedSections = async (page: number, searchTerm?: string, pageSize?: number) => {
        const searchResult = await eaiClient.getUnmmapedSections(page, searchTerm ?? "", pageSize ?? state.pageSize);
        setState((prev) => ({
            ...prev,
            sections: searchResult.data.data,
            sectionsPage: page,
            sectionsTitle: searchTerm ?? "",
            pageSize: pageSize ?? prev.pageSize
        }));
    };

    const setSectionSelection = async (sectionId: number, checkValue: boolean) => {
        const value = !state.sectionsSelection.length ? [sectionId] : checkValue ? state.sectionsSelection.concat([sectionId]) : state.sectionsSelection.filter((e) => e !== sectionId);
        setState((prev) => ({ ...prev, sectionsSelection: value }));
    };

    const setSectionSearch = async (value: string) => {
        const lowerCaseValue = value.toLowerCase();
        setState(prev => ({ ...prev, sectionsTitle: value }));
        if (value.length > 0) {
            await getUnmappedSections(1, lowerCaseValue, state.pageSize);
        } else {
            await getUnmappedSections(1, "", state.pageSize);
        }
    };

    const renderTable = () => {
        if (props.isLoading) {
            return (
                <p className="text-center">
                    <Spinner variant="success" />
                </p>
            );
        }

        return (
            <div className="unmapped__section">
                <div className="unmapped__section-table">
                    <RenderTable
                        data={unmappedSections?.data.data ?? []}
                        columns={columnsSections}
                        noDataMessage={state.sectionsTitle ? `No results found for ${state.sectionsTitle}` : "There are not unmapped sections at the moment"}
                        className="sectionTable"
                        totalItems={unmappedSections?.data.total_filtered}
                        currentPage={state.sectionsPage - 1}
                        setCurrentPage={async (page) => {
                            setState((prev) => ({ ...prev, sectionsPage: page + 1 }));
                            await getUnmappedSections(page + 1, state.sectionsTitle);
                        }}
                        pageSize={state.pageSize}
                        setPageSize={async (pageSize) => {
                            setState((prev) => ({ ...prev, sectionsPage: 1, pageSize }));
                            await getUnmappedSections(1, state.sectionsTitle, pageSize);
                        }}
                        isLoading={isLoadingUnmappedSections}
                        sorting={state.sorting}
                        setSorting={updater => {
                            if (typeof updater === "function") {
                                const newSorting = updater(state.sorting);
                                setState(prevState => ({
                                    ...prevState,
                                    sorting: newSorting,
                                }));
                            } else if (typeof updater === "object") {
                                setState(prevState => ({
                                    ...prevState,
                                    sorting: updater,
                                }));
                            }
                        }}
                    />
                </div>
            </div>
        );
    };

    // handle goback
    const handleGoBackToCourseCatalog = () => {
        // reset inputs on goback
        setSectionSearch("");
        props.setShowCourseCatalog();
    };

    const onSubmitMap = async () => {
        try {
            setState((prev) => ({ ...prev, isMapping: true }));
            const { data: data } = (await eaiClient.mapSections(state.sectionsSelection, state.selectedCourse?.rid as number)) as AxiosResponse<IMapSectionResult, any>;

            if (data.result.code === 201) {
                toast({
                    type: "success",
                    title: "Sections Mapped Successfully",
                    message: getSuccessMessage()
                });
                mutateUnmappedSections();
                props.refreshCourseCatalog();
            }
        } catch (error) {
            appContext.dispatchError(error);
        } finally {
            setState((prev) => ({
                ...prev,
                isMapping: false,
                openMap: false,
                selectedCourse: null,
                sectionsSelection: []
            }));
        }
    };

    return (
        <div className="unmappedContainer">
            <div className="unmappedTitle">
                <span onClick={handleGoBackToCourseCatalog} className="backCourses">
                    <AngleLeftIcon />
                </span>
                Unmapped sections ({unmappedSections?.data.total || 0})
            </div>

            <div className="section px-0">
                <p className="instruction mb-0">Instruction: Select section(s) from the table below and map to a course.</p>
            </div>

            <div className="section px-0">
                <div className="search__bar">
                    <Search className="type icon icon-search search__bar-icon" aria-hidden="true" data-testid="icon-search" />
                    <DebouncedInput
                        type="text"
                        value={state.sectionsTitle}
                        onChange={(value) => setSectionSearch(value.toString())}
                        placeholder="Search by section title or subject number"
                        className="search-input"
                        debounce={1000}
                    />
                    {state.sectionsTitle && (
                        <Button variant="ghost" className="search__bar-icon" onClick={() => setSectionSearch("")}>
                            <X size={20} />
                        </Button>
                    )}
                </div>

                <div>
                    <Button id="mapCourse" variant="primary" className="section__button section__button-active" disabled={!getSelectedLength() || state.confirmMapping} onClick={() => setState((prev) => ({ ...prev, openMap: true }))}>
                        <Ellipsis className="me-2" />
                        Map {`${getSelectedLength() || ""}`} {`Section${getSelectedLength() > 1 ? "s" : ""}`}
                    </Button>
                </div>
            </div>

            {renderTable()}

            <CoursesModal
                open={state.openMap}
                onClose={() => setState((prev) => ({ ...prev, openMap: false }))}
                courses={(courseCatalog?.data as CoursesCatalogInterface[]) ?? []}
                state={state} setState={setState}
                sections={(unmappedSections?.data.data as SectionsCatalogInterface[]) ?? []}
                onSubmitMap={onSubmitMap}
            />
            <StyledModal
                isOpen={disableModalOpen}
                onClose={() => { setDisableModalOpen(false); setSelectedSection(null); setDisableReason(""); }}
                title={`Are you sure you want to ${selectedSection?.disabled ? "activate" : "deactivate"} this section?`}
                subtitle={`To continue, you need to share the reason for ${selectedSection?.disabled ? "activating" : "deactivating"} this section`}
                cancelButtonLabel="Cancel"
                confirmButtonLabel={selectedSection?.disabled ? "Activate" : "Deactivate"}
                onSubmit={handleDisableSubmit}
                isLoading={disableLoading}
            >
                <Form.Group>
                    <Form.Label>Override comment</Form.Label>
                    <Form.Control
                        as="input"
                        name="reason"
                        minLength={5}
                        required
                        value={disableReason}
                        onChange={e => setDisableReason(e.target.value)}
                    />
                </Form.Group>
            </StyledModal>
        </div>
    );
};

const RenderTable = (
    {
        columns, data, noDataMessage, sorting, setSorting, className, totalItems, currentPage, setCurrentPage, pageSize, setPageSize, isLoading, refresh
    }: unmappedTableProps) => {
    const totalPages = Math.ceil((totalItems ?? 0) / (pageSize || 10));

    if (totalPages === 0) {
        return <StyledTable
            className={className}
            defaultData={data}
            columns={columns}
            noDataMessage={noDataMessage}
            showRowsPerPage={false}
            autoResetPageIndex={refresh}
        />;
    }

    return <StyledTable
        initialPageSize={pageSize || 10}
        className={className}
        defaultData={data}
        columns={columns}
        noDataMessage={noDataMessage}
        setPageSize={setPageSize}
        totalPagesNumber={totalPages}
        currentPageNumber={currentPage}
        setCurrentNumber={setCurrentPage}
        isLoading={isLoading}
        sorting={sorting}
        setSorting={setSorting}
        manualSorting={true}
    />;
};
